<template>
  <label
    class="cf-upsell-item"
    :for="`upsellItem-${upsellMenuItem.menuItemId}`"
  >
    <div class="cf-upsell-item__layout">
      <div class="cf-upsell-item-details">
        <!-- Radio Checkbox -->
        <div class="cf-checkbox-input cf-checkbox-input--radio cf-checkbox-input--default cf-checkbox-input--no-label-text cf-clear-margin">
          <div class="cf-checkbox-input__option">
            <input
              :id="`upsellItem-${upsellMenuItem.menuItemId}`"
              class="cf-checkbox-input__input"
              type="checkbox"
              name="upsellItem"
              :value="isItemChecked"
              @input="upsellItemClickHandler"
            >

            <div class="cf-checkbox-input__label" />
          </div>
        </div>

        <div class="cf-upsell-item-details__description">
          <!-- Upsell Item Title/Name -->
          <h4 class="cf-title cf-title--4 dark cf-no-wrap-ellipsis-multi-line">
            {{ upsellMenuItem.displayName }}
          </h4>

          <!-- Upsell Item Description -->
          <p
            v-if="upsellMenuItem.descriptionText"
            class="cf-subtitle cf-subtitle--3 cf-no-wrap-ellipsis-multi-line"
          >
            {{ upsellMenuItem.descriptionText }}
          </p>

          <!-- Upsell Item Modifier Summary -->
          <p
            v-if="upsellMenuItem.modifierSummary"
            class="cf-subtitle cf-subtitle--3"
          >
            {{ upsellMenuItem.modifierSummary }}
          </p>

          <!-- Upsell Item Image -->
          <h5
            v-if="!(upsellItemPrice === 0 && merchant.options.hideZeroPricedItemPrices)"
            class="cf-title cf-title--5"
          >
            {{ upsellItemPrice }}
          </h5>
        </div>

        <!-- Upsell Item Image -->
        <img
          v-if="imageUrl"
          class="cf-menu-list__item-image cf-menu-list__item-image--upsell-img"
          :src="imageUrl"
          onerror="this.src='/images/image-not-found.png';"
        >
      </div>

      <!-- Upsell Item Qty Selector -->
      <div
        v-if="isItemChecked"
        class="cf-upsell-qty-selector"
        @click.stop
      >
        <cf-quantity-input
          :model-value="quantity"
          :max-value="maxValue"
          :is-full-width="true"
          :clear-border-style="true"
          name="menuItemQuantity"
          @update:model-value="quantityInputClickHandler"
        />
      </div>
    </div>
  </label>
</template>


<script>
  import { mapGetters } from 'vuex';

  import dollars from '@/filters/dollars';

  import CfQuantityInput from '@/view/components/shared/cf-quantity-input/index.vue';


  export default {
    name: 'UpsellItem',

    components: {
      CfQuantityInput
    },

    props: {
      upsellMenuItem: {
        type: Object,
        required: true
      },

      selectedUpsellItems: {
        type: Array,
        required: true
      },

      selectedUpsellItemCount: {
        type: Number,
        required: true
      }
    },

    data() {
      return {
        isItemChecked: false,
        quantity: 0
      };
    },

    computed: {
      ...mapGetters('orders', ['order', 'orderValidations']),
      ...mapGetters('session', ['isTableTopDeviceOrdering']),
      ...mapGetters('merchant', ['merchant']),

      imageUrl() {
        const image = this.upsellMenuItem.images.find(img => img.label === 'menu');

        return image ? image.url : null;
      },

      maxValue() {
        const { orderMaximumItemCount } = this.orderValidations.validations;

        const isUnlimitedItem = this.upsellMenuItem.maxAllowed === -1;

        const itemMax = this.upsellMenuItem.maxAllowed;

        if (orderMaximumItemCount) {
          // TODO: Verify if we want to limit max to include other selected upsell items
          let cartMax = Math.max(orderMaximumItemCount - (this.order.orderItemCount + this.quantity), 0);

          cartMax += this.quantity;

          return isUnlimitedItem ? cartMax : Math.min(itemMax, cartMax);
        }

        return itemMax;
      },

      upsellItemPrice() {
        const price = dollars(this.upsellMenuItem.pricePerUnitAfterMods || this.upsellMenuItem.price);

        return price;
      },

      itemModifiers() {
        if (this.upsellMenuItem.modifierGroups.length) {
          const modifiers = [];

          this.upsellMenuItem.modifierGroups.forEach((group) => {
            group.modifiers.forEach((modifier) => {
              if (modifier.isDefault) {
                modifiers.push({ menuItemId: modifier.menuItemId, quantity: 1 });
              }
            });
          });

          return modifiers;
        }

        return [];
      }
    },

    methods: {
      quantityInputClickHandler(newVal) {
        this.quantity = newVal;

        this.selectUpsellItem();
      },

      upsellItemClickHandler(event) {
        this.isItemChecked = event.target.checked;

        if (this.isItemChecked) {
          this.checkUpsellItem();
        }
        else {
          this.uncheckUpsellItem();
        }
      },

      checkUpsellItem() {
        this.quantity = 1;

        // Note: If an item cannot be quick added, open the detail modal so user can make initial mod/qty selection.
        // Once user has made initial selections user cannot re-open the detail view.
        if (this.upsellMenuItem.quickAddEnabled) {
          this.selectUpsellItem();
        }
        else {
          this.openUpsellItem();
        }
      },

      uncheckUpsellItem() {
        this.quantity = 0;

        this.$emit('unselect-upsell-item', this.upsellMenuItem);
      },

      selectUpsellItem(updatedProperties) {
        const payload = {
          categoryId: this.upsellMenuItem.menuCategoryId,
          menuItemId: this.upsellMenuItem.menuItemId,
          name: this.upsellMenuItem.displayName,
          isUpsell: this.upsellMenuItem.isUpsell,
          maxAllowed: this.upsellMenuItem.maxAllowed,
          price: this.upsellMenuItem.price,
          quantity: this.quantity,
          modifiers: this.itemModifiers,
          ...updatedProperties
        };

        this.$emit('select-upsell-item', payload);
      },

      openUpsellItem() {
        this.$triggerNewRelicEvent('UpsellItemViewed', 'Information', 'Upsell Item Viewed');

        const modalPayload = {
          upsellMenuItem: this.upsellMenuItem,
          updateUpsellItemData: this.updateUpsellItemData
        };

        this.$openModal('upsellItemDetailsModal', modalPayload);
      },

      updateUpsellItemData(upsellItemDetailData) {
        // Note: Set the quantity of the current upsell card to the quantity selected in the details view.
        this.quantity = upsellItemDetailData.quantity;

        this.selectUpsellItem(upsellItemDetailData);
      }
    }
  };
</script>
